import { FC } from 'react'

import styled from 'styled-components'

import Trash from 'src/Assets/trash.svg?react'
import DefaultEquipment from 'src/Assets/equipment.svg'
import { colors, device } from 'src/Helpers/constants.ts'
import { IconButton } from 'src/Components/Button/IconButton.tsx'
import { Equipment } from 'src/Redux/api/equipmentApi'
import { getValueOrFallback, numberToMoney } from 'src/Helpers/functions'
import { Image } from 'src/Components/Image'

const StyledImage = styled(Image)`
  display: flex;
  width: 50px;
  height: 50px;
  align-items: center;
  object-fit: cover;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  gap: 10px;
  background-color: ${colors.mainGray};
  padding: 10px;
`

const ParametersContainer = styled.div`
  position: relative;
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: 0.6fr 0.5fr 0.3fr 0.5fr 0.8fr 0.8fr 0.4fr;

  @media ${device.tabletMax} {
    grid-template-columns: 1fr;
    row-gap: 12px;
  }
`

const Name = styled.span`
  font-size: 14px;
  max-width: 200px;
  width: 100%;
  text-align: left;
  color: ${colors.thirdGray};
  font-weight: 600;
`

const Parameter = styled.div`
  font-size: 12px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: ${colors.thirdGray};
  font-weight: 600;
  justify-content: center;
  text-align: left;
  @media ${device.tabletMax} {
    text-align: start;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  @media ${device.tabletMax} {
    position: absolute;
    top: 0;
    right: 0;
  }
`

const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  width: 250px;
  cursor: pointer;

  @media ${device.tabletMax} {
    width: 100%;
    gap: 12px;
  }
`

const ToolName = styled.span`
  font-size: 12px;
  color: ${colors.thirdGray};
`

const StyledButton = styled(IconButton)`
  background-color: ${colors.red};

  &:hover {
    background-color: ${colors.red};
  }
  margin-left: auto;
`

const NotOwnedBadge = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  background-color: ${colors.secondGray};
  color: ${colors.thirdBlue};
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
`

const EquipmentWrapper = styled.div`
  align-self: center;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  color: ${colors.thirdBlue};
  font-weight: 600;
  font-size: 12px;
`

interface CatalogItemProps {
  onArchiveItem: (id: number) => void
  onEditItem: (id: number) => void
  equipment: Equipment
  isArchiveTransportAvailable: boolean
}

export const EquipmentItem: FC<CatalogItemProps> = ({
  onArchiveItem,
  onEditItem,
  equipment,
  isArchiveTransportAvailable,
}) => {
  if (!equipment) {
    return null
  }

  const { id, year, model, isOwned } = equipment

  const onArchiveItemClick = (): void => {
    onArchiveItem(id)
  }

  const onEditItemClick = (): void => {
    onEditItem(id)
  }

  return (
    <Container>
      <ParametersContainer>
        <NameWrapper
          onClick={onEditItemClick}
          data-testid='equipment-item-name'
        >
          <StyledImage src={model.imageUrl} fallbackSrc={DefaultEquipment} />
          <Name>{model.name}</Name>
          {isOwned === false && (
            <NotOwnedBadge data-testid='equipment-not-owned'>
              Не в собственности
            </NotOwnedBadge>
          )}
        </NameWrapper>

        <Parameter>{getValueOrFallback(model?.modelType?.label)}</Parameter>
        <Parameter>
          {numberToMoney(Number(getValueOrFallback(model.baseRentalCost)))}
        </Parameter>
        <Parameter>{getValueOrFallback(year)}</Parameter>

        <Parameter>{getValueOrFallback(model.description)}</Parameter>
        <EquipmentWrapper>
          <ToolName>Доступное навесное оборудование:</ToolName>

          {model?.modelTools?.map((tool) => {
            return <ToolName key={tool.id}>- {tool.name}</ToolName>
          })}
        </EquipmentWrapper>
        <ButtonContainer>
          <StyledButton
            onClick={onArchiveItemClick}
            disabled={!isArchiveTransportAvailable}
            tooltiptext={'Отправить технику в архив'}
            data-testid='delete-equipment-button'
          >
            <Trash />
          </StyledButton>
        </ButtonContainer>
      </ParametersContainer>
    </Container>
  )
}
